import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBell, FaBroadcastTower, FaCalendarAlt, FaDollarSign, FaMusic } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useNotificationsContext } from '../../../contexts/NotificationsContext.jsx';
import { describeNotification } from '../../../_helpers/notifications.js';
import { formatRelativeTime } from '../home/HomeNotificationsCard.jsx';
import { focusFirstIn } from './headerMenuFocus.js';

/** How many notifications the dropdown shows; See all opens the rest. */
export const DROPDOWN_LIMIT = 6;

/** The icon of a notification type. */
// By the type's first part: live_stream, show, tip or request (ENG-726).
const ICONS = { live_stream: FaBroadcastTower, show: FaCalendarAlt, tip: FaDollarSign, request: FaMusic };
const iconFor = (type = '') => ICONS[String(type).split('.')[0]] ?? FaBell;

/**
 * The bell's dropdown (mockups 30 and 31, ENG-725/727): "Notifications · N
 * new" with Mark all as read, the newest notifications with their icon,
 * relative time and unread dot — each opens where it points and is marked
 * read — then Notification settings and See all. With nothing there it says
 * "You're all caught up." The list is the app's notification feed, which the
 * live stream keeps current (ENG-440/470). Esc and a click outside close it.
 */
const NotificationsDropdown = ({ onClose, inline = false, id }) => {
  const { t, format, locale } = useLocale();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { items, unread, markRead, markAllRead } = useNotificationsContext();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Focus moves into the menu as it opens (ENG-840).
  useEffect(() => { focusFirstIn(ref.current); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose('escape'); };
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[data-notifications-trigger]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const shown = useMemo(() => (Array.isArray(items) ? items.slice(0, DROPDOWN_LIMIT) : []), [items]);
  const count = unread?.count ?? 0;

  const open = (item) => {
    if (!item.read) markRead(item.id);
    onClose();
    if (item.destination?.kind === 'route') navigate(item.destination.to);
    else if (item.destination?.kind === 'url') window.open(item.destination.href, '_blank', 'noopener');
  };

  return (
    <div
      className={`header-menu notifications-dropdown${inline ? ' header-menu--inline' : ''}`}
      ref={ref}
      id={id}
      role={inline ? 'region' : 'dialog'}
      aria-label={t('notifications.title')}
      data-testid="notifications-dropdown"
    >
      <div className="notifications-dropdown-head">
        <p className="notifications-dropdown-title">
          {t('notifications.title')}
          {count > 0 && <span className="notifications-dropdown-new"> · {t('stream.notificationsMenu.new', { count, formattedCount: format.number(count) })}</span>}
        </p>
        {count > 0 && (
          <button type="button" className="notifications-dropdown-markall" onClick={() => markAllRead()}>
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="notifications-dropdown-empty" data-testid="notifications-empty">
          <FaBell aria-hidden="true" />
          <p className="notifications-dropdown-empty-title">{t('stream.notificationsMenu.caughtUp')}</p>
          <p>{t('stream.notificationsMenu.caughtUpHint')}</p>
        </div>
      ) : (
        <ul className="notifications-dropdown-list">
          {shown.map((item) => {
            const { label, detail } = describeNotification(item, t, locale);
            const Icon = iconFor(item.type);
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`notifications-dropdown-item${item.read ? '' : ' notifications-dropdown-item--unread'}`}
                  onClick={() => open(item)}
                  data-testid={`notifications-item-${item.id}`}
                >
                  <span className="notifications-dropdown-icon" aria-hidden="true"><Icon /></span>
                  <span className="notifications-dropdown-text">
                    <span className="notifications-dropdown-label">{label}</span>
                    {detail && <span className="notifications-dropdown-detail">{detail}</span>}
                  </span>
                  <span className="notifications-dropdown-meta">
                    <time dateTime={item.createdAt.toISOString()}>{formatRelativeTime(item.createdAt, now, { t, format })}</time>
                    {!item.read && <span className="notifications-dropdown-dot" aria-label={t('stream.notificationsMenu.unread')} />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="notifications-dropdown-foot">
        <Link to="/notifications" onClick={onClose}>{t('stream.notificationsMenu.settings')}</Link>
        <Link to="/notifications" onClick={onClose}>{t('stream.notificationsMenu.seeAll')}</Link>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
